import { css, type DefaultTheme } from 'styled-components'
import type { Density } from '~/packages/general/variable/variables.typing'

export const menuWidth = 220
export const mobileMaxWidth = 768

export const getContentWidth = ({ windowWidth }: { windowWidth: number }) =>
  windowWidth >= mobileMaxWidth ? windowWidth - menuWidth : windowWidth

export const gradients = {
  nausPurple: ['#9e7ae6', '#5f27cd'],
}

export const colors = {
  // Prix Palette
  prixBlue: '#0C2340', // Pantone 289C
  prixOrange: '#FF6900', // Pantone 1505C
  prixGreen: '#26D07C', // Pantone 7479C

  prixLightOrange: 'rgba(255, 105, 0, 0.5)',

  // Other projects pallete
  // Other projects palette
  selectedBlue: '#9DD1FF',
  gray: '#ECF0F1',
  darkGray: '#777',
  wetAsphalt: '#486280',
  nausPurple: '#5F27CD',
  bluebell: '#341F97',
  oceanBlue: '#0F438A',
  brandBlue: '#005EB8',
  brandBlueDarker: '#0050A0',
  lightBlue: '#ABD6FF',
  midNightBlue: '#341F97',
  headerBlue: '#0F438A',
}

export const complementarColors = {
  // Other colors
  amethyst: '#9b59b6',
  emerald: '#2ecc71',
  turquoise: '#1abc9c',
  alizarin: '#e74c3c',
  orange: '#f39c12',
  nephritis: '#27ae60',
  pomegranate: '#c0392b',
  yellow: '#FFD400',
  deviantPurple: '#730CE5',
  sonderPurple: '#BD8DF3',
  backgroundWhite: '#F3F3F3',
  rosePearl: '#F14668',
}

export const institutionalColors = {
  brand10: '#0A1729',
  brand20: '#142E52',
  brand30: '#0F438A',
  brand40: '#005EB8',
  brand60: '#5C8FD6',
  brand75: '#8FB8EF',
  brand90: '#394D60',
  brand97: '#F0F6F3',
  neutral10: '#121A21',
  neutral20: '#243342',
  neutral30: '#394D60',
  neutral45: '#617385',
  neutral60: '#8B99A7',
  neutral75: '#B6BFC8',
  neutral90: '#E0E5EB',
  neutral97: '#F5F7FA',
}

export const organizationalColors = {
  aquaBlue10: '#05282E',
  aquaBlue20: '#094F5D',
  aquaBlue30: '#0E778D',
  aquaBlue44: '#14ADCC',
  aquaBlue60: '#58C4DA',
  aquaBlue75: '#96DBEE9',
  aquaBlue90: '#D5F1F6',
}

export const primaryColors = {
  darkest: institutionalColors.brand10,
  action: institutionalColors.brand30,
  default: institutionalColors.brand40,
  active: institutionalColors.brand60,
  lightest: institutionalColors.brand97,
}

export const colorMaps = {
  // Choropleth usage
  toHeat: ['#26d07c', '#ffd400', '#e74c3c'], // More is hot
  toCold: ['#e74c3c', '#ffd400', '#26d07c'], // More is cold

  // Heatmap usage
  toHeatHeatmap: ['#ffd400', '#e74c3c', '#c0392b'], // More is hot
  toColdHeatmap: ['#ffd400', '#26d07c'], // More is cold

  // Self comparison usage
  toHeatNeutralMiddle: ['#26d07c', '#808080', '#e74c3c'], // More is hot
  toColdNeutralMiddle: ['#e74c3c', '#808080', '#26d07c'], // More is cold
}
export const getColorMap = (betterDensity: Density, target: 'choropleth' | 'heatmap') => {
  const colorMap =
    target === 'choropleth'
      ? colorMaps[betterDensity === 'less' ? 'toHeat' : 'toCold']
      : colorMaps[betterDensity === 'less' ? 'toHeatHeatmap' : 'toColdHeatmap']
  return colorMap
}

export const blueTones = [
  '#0d47a1',
  '#1565c0',
  '#1976d2',
  '#1e88e5',
  '#2196f3',
  '#42a5f5',
  '#64b5f6',
  '#5fa8ef',
  '#7abdf4',
  '#90caf9',
]

const theme: DefaultTheme = {
  colors: {
    primary: colors.prixBlue,
    secondary: colors.prixOrange,
    tertiary: colors.prixGreen,
    selectedBlue: colors.selectedBlue,
    selectedItem: '#f3f3f3',
    inputBorder: '#2684ff',
    lightGrey: '#ecf0f1',
    mediumGrey: '#ccc',
    darkGrey: '#414141',
    success: '#2ecc71',
    danger: '#e74c3c',
    darkText: '#0e1c1a',
    lightText: '#fff',
  },
  borderRadius: '8px',
  shadow: level => `0 1px ${level * 10}px rgba(35, 35, 35, ${0.1 + level * 0.12})`,
  padding: level =>
    `${
      {
        0: 0,
        1: 0.3,
        2: 1,
      }[level]
    }rem`,
  // backdropFilter: 'blur(8px) saturate(180%)',
  // backdropBackground: 'rgba(255, 255, 255, 0.75)',
  backdropFilter: 'none',
  backdropBackground: 'white',
  transitionDurationMs: 200,
  transitionTimingFunction: 'ease-in',
  breakpoints: {
    mobile: 768,
  },
} as const
export default theme

export const selectStyles = {
  container: (base: any) => ({
    ...base,
    flex: 1,
    minWidth: '100%',
    maxWidth: '600px',
  }),
  control: (provided: any, state: any) => ({
    ...provided,
    borderRadius: '8px',
    borderColor: provided.isFocused ? colors.prixBlue : '#ccc',

    '&:hover': {
      borderColor: state.isFocused ? colors.prixBlue : '#ccc',
    },
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? colors.prixOrange
      : state.isFocused
      ? colors.prixLightOrange
      : 'white',
    color: state.isSelected ? 'white' : 'black',
    '&:hover': {
      backgroundColor: state.isSelected ? colors.prixOrange : colors.prixLightOrange,
    },
  }),
}

export const checkboxStyle = css`
  min-height: 20px;
  min-width: 20px;
  -webkit-appearance: none;
  appearance: none;
  background: ${({ theme }) => theme.colors.lightGrey};

  &:checked {
    background: ${({ theme }) => theme.colors.primary};
    position: relative;

    &::before {
      content: '';
      background: no-repeat center;
      background-size: 10px auto;
      background-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA0NDggNTEyIj4NCiAgPHBhdGgNCiAgICBkPSJNNDM4LjYgMTA1LjRjMTIuNSAxMi41IDEyLjUgMzIuOCAwIDQ1LjNsLTI1NiAyNTZjLTEyLjUgMTIuNS0zMi44IDEyLjUtNDUuMyAwbC0xMjgtMTI4Yy0xMi41LTEyLjUtMTIuNS0zMi44IDAtNDUuM3MzMi44LTEyLjUgNDUuMyAwTDE2MCAzMzguNyAzOTMuNCAxMDUuNGMxMi41LTEyLjUgMzIuOC0xMi41IDQ1LjMgMHoiDQogICAgZmlsbD0iI0ZGRkZGRiIgLz4NCjwvc3ZnPg==');
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 2;
    }
  }
`

export const rangeStyle = css`
  width: 100%;
  -webkit-appearance: none;
  appearance: none;
  height: 10px;
  border-radius: 5px;
  cursor: pointer;

  background: linear-gradient(
    to right,
    ${({ theme }) => theme.colors.primary} 0%,
    ${({ theme }) => theme.colors.primary} var(--progress),
    ${({ theme }) => theme.colors.lightGrey} var(--progress),
    ${({ theme }) => theme.colors.lightGrey} 100%
  );

  &::-webkit-slider-runnable-track {
    height: 10px;
    border-radius: 5px;
  }

  &::-moz-range-progress {
    background-color: ${({ theme }) => theme.colors.primary};
    height: 10px;
    border-radius: 5px;
  }

  &::-moz-range-track {
    background-color: ${({ theme }) => theme.colors.lightGrey};
    height: 10px;
    border-radius: 5px;
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    background: ${({ theme }) => theme.colors.primary};
    border-radius: 50%;
    cursor: pointer;
    margin-top: -5px;
  }

  &::-moz-range-thumb {
    border: none;
    width: 20px;
    height: 20px;
    background: ${({ theme }) => theme.colors.primary};
    border-radius: 50%;
    cursor: pointer;
  }
`
